/**
 * Clock / API notes may append a line like `[Coordinates: 24.860735, 67.001137]` for map display.
 */

import { WebStorage, WebStorageNames } from "../../../utils";
import { getUserInfo } from "../../../api/axios";

const COORDS_IN_NOTE_RE = /\[Coordinates:\s*([-\d.]+),\s*([-\d.]+)\]/i;

/** @returns {number|null} */
export const getAttendanceDailyLogsRoleId = () => {
  const r = WebStorage.getLocalStore(WebStorageNames.RoleId) ?? localStorage.getItem("roleId");
  const n = parseInt(String(r), 10);
  return Number.isFinite(n) ? n : null;
};

/** Company / super admin — same as header `isAdmin`. */
export const isAttendanceDailyLogsAdminRole = (rid) => rid === 1 || rid === 2;

/** Whether the logged-in user has a non-empty leave/attendance approver id (from auth payload). */
export const hasAttendanceApproverId = () => {
  const u = getUserInfo();
  const aid = u?.approverId ?? u?.ApproverId;
  return aid != null && String(aid).trim() !== "";
};

/**
 * When true, Daily Logs ignores office flags “allow employees to edit/delete their own events”
 * so company admins without an assigned approver can still use Edit/Delete (session rules still apply).
 */
export const bypassEmployeeEditDeleteOfficeFlags = () =>
  isAttendanceDailyLogsAdminRole(getAttendanceDailyLogsRoleId()) && !hasAttendanceApproverId();

/**
 * @param {string|null|undefined} note
 * @returns {{ lat: number, lng: number }|null}
 */
export const parseCoordinatesFromAttendanceNote = (note) => {
  if (note == null || typeof note !== "string") return null;
  const m = note.match(COORDS_IN_NOTE_RE);
  if (!m) return null;
  const lat = parseFloat(m[1]);
  const lng = parseFloat(m[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
};

/**
 * Note text with the coordinate line removed (avoids duplicating map data in the Note column).
 * @param {string|null|undefined} note
 * @returns {string}
 */
export const noteTextWithoutCoordinates = (note) => {
  if (note == null || typeof note !== "string") return "";
  if (isInternalBiometricNote(note)) return "";
  return note.replace(COORDS_IN_NOTE_RE, "").replace(/\n{3,}/g, "\n\n").trim();
};

/** Agent ingest used to store device key + enroll here; that is not for employees. */
export const isInternalBiometricNote = (note) => {
  const text = String(note ?? "").trim();
  if (!text) return false;
  return /^biometric\s+(device|reprocess)\b/i.test(text);
};

/** Hub location column: real location, else a friendly machine name — never device keys / enroll numbers. */
export const displayAttendancePlaceLabel = (location, note) => {
  const loc = String(location ?? "").trim();
  if (loc) return loc;
  return noteTextWithoutCoordinates(note);
};
